import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Siren, ArrowLeft, Lock, Printer, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const dataOra = (iso) => (iso ? new Date(iso).toLocaleString("it-IT",
  { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const Tris = ({ value, onSet, testid }) => (
  <div className="flex gap-1">
    {[["SÌ", true], ["NO", false]].map(([label, v]) => (
      <button key={label} type="button" data-testid={`${testid}-${v ? "si" : "no"}`}
        onClick={() => onSet(value === v ? null : v)}
        className={`h-8 min-w-[46px] rounded-sm border text-xs font-bold transition-colors duration-150 ${
          value === v
            ? (v ? "border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                 : "border-rose-500 bg-rose-500/15 text-rose-700 dark:text-rose-300")
            : "border-border hover:bg-secondary"}`}>
        {label}
      </button>
    ))}
  </div>
);

export default function EmergencyRegister() {
  const { user } = useAuth();
  const canWrite = ["admin", "manager"].includes(user?.role) || user?.is_preposto;
  const canDelete = ["admin", "manager"].includes(user?.role);
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState("");
  const [q, setQ] = useState("");

  const loadSessions = useCallback(() => {
    api.get("/emergency/sessions").then((r) => setSessions(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  const removeSession = async (s) => {
    if (!window.confirm(`Eliminare la sessione del ${dataOra(s.started_at)}?`)) return;
    try {
      await api.delete(`/emergency/sessions/${s.id}`);
      toast.success("Sessione eliminata");
      loadSessions();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openSession = async (id) => {
    try { setOpen((await api.get(`/emergency/sessions/${id}`)).data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const start = async () => {
    try {
      const { data } = await api.post("/emergency/sessions", { note });
      setNote("");
      toast.success("Registro emergenze aperto");
      loadSessions();
      openSession(data.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setField = async (emp, campo, valore) => {
    try {
      const { data } = await api.put(`/emergency/sessions/${open.id}/entry`,
        { employee_id: emp.employee_id, [campo]: valore });
      setOpen({
        ...open, counters: data.counters,
        employees: open.employees.map((e) => e.employee_id === emp.employee_id
          ? { ...e, present: data.present, evacuated: data.evacuated, injured: data.injured } : e),
      });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const scaricaPdf = async () => {
    try {
      const res = await api.get(`/emergency/sessions/${open.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const close = async () => {
    if (!window.confirm("Chiudere la sessione? Dopo la chiusura il registro non è più modificabile.")) return;
    try {
      await api.post(`/emergency/sessions/${open.id}/close`);
      toast.success("Sessione chiusa e archiviata");
      loadSessions();
      openSession(open.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (open) {
    const chiusa = !!open.closed_at;
    const filtro = q.trim().toUpperCase();
    const lista = open.employees.filter((e) => !filtro
      || `${e.name} ${e.code} ${e.department || ""}`.toUpperCase().includes(filtro));
    return (
      <div className="space-y-3" data-testid="emergency-session-page">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <Button variant="outline" size="sm" className="rounded-sm mb-2" data-testid="emergency-back"
              onClick={() => { setOpen(null); loadSessions(); }}>
              <ArrowLeft size={14} className="mr-1.5" /> Torna al registro
            </Button>
            <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">
              Emergenza del {dataOra(open.started_at)}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Compilato da <span className="font-semibold text-foreground">{open.created_by}</span>
              {open.note ? ` · ${open.note}` : ""}
              {chiusa ? ` · chiusa il ${dataOra(open.closed_at)}` : ""}
            </p>
          </div>
          {!chiusa && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-sm font-semibold" data-testid="emergency-pdf"
                onClick={scaricaPdf}>
                <Printer size={14} className="mr-1.5" /> PDF A4
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm font-semibold" data-testid="emergency-close"
                onClick={close}>
                <Lock size={14} className="mr-1.5" /> Chiudi sessione
              </Button>
            </div>
          )}
          {chiusa && (
            <Button size="sm" variant="outline" className="rounded-sm font-semibold" data-testid="emergency-pdf"
              onClick={scaricaPdf}>
              <Printer size={14} className="mr-1.5" /> PDF A4
            </Button>
          )}
        </div>

        <div className="rounded-sm border border-border bg-card p-3 flex flex-wrap items-center gap-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca dipendente o reparto..."
            className="h-9 w-[240px] rounded-sm" data-testid="emergency-search" />
          <div className="flex flex-wrap gap-3 text-xs font-semibold tabular-nums" data-testid="emergency-counters">
            <span>Compilati: {open.counters.compiled}/{open.employees.length}</span>
            <span className="text-emerald-700 dark:text-emerald-300">In azienda: {open.counters.present}</span>
            <span className="text-sky-700 dark:text-sky-300">Evacuati: {open.counters.evacuated}</span>
            <span className="text-rose-700 dark:text-rose-300">Feriti: {open.counters.injured}</span>
          </div>
        </div>

        <div className="rounded-sm border border-border bg-card divide-y divide-border">
          {lista.map((e) => (
            <div key={e.employee_id} className="flex flex-wrap items-center gap-3 px-3 py-2"
              data-testid={`emergency-row-${e.code}`}>
              <div className="min-w-[210px]">
                <div className="text-sm font-semibold">{e.name}</div>
                <div className="text-[11px] text-muted-foreground">{e.code} · {e.department || "—"}</div>
              </div>
              {[["Presente in azienda", "present"], ["Evacuato", "evacuated"], ["Ferito", "injured"]]
                .map(([label, campo]) => (
                  <div key={campo}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
                    {chiusa
                      ? <div className="h-8 flex items-center text-xs font-bold" data-testid={`emergency-${campo}-${e.code}-val`}>
                          {e[campo] === true ? "SÌ" : e[campo] === false ? "NO" : "—"}
                        </div>
                      : <Tris value={e[campo]} testid={`emergency-${campo}-${e.code}`}
                          onSet={(v) => setField(e, campo, v)} />}
                  </div>
                ))}
            </div>
          ))}
          {lista.length === 0 && (
            <div className="px-3 py-8 text-center text-muted-foreground" data-testid="emergency-empty">
              Nessun dipendente trovato
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="emergency-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Registro Emergenze</h1>
        <p className="text-sm text-muted-foreground mt-1">
          In caso di evacuazione apri una nuova sessione e segna per ogni dipendente se è presente in
          azienda, se è stato evacuato e se è ferito. Data, ora e nome di chi compila vengono registrati.
        </p>
      </div>

      <div className="rounded-sm border border-rose-300 dark:border-rose-900 bg-rose-50/60 dark:bg-rose-950/30 p-3 flex flex-wrap items-center gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Motivo (es. incendio reparto macello) — facoltativo"
          className="h-9 w-[320px] rounded-sm" data-testid="emergency-note" />
        <Button size="sm" className="rounded-sm font-semibold" data-testid="emergency-start" onClick={start}>
          <Siren size={15} className="mr-1.5" /> APRI REGISTRO EMERGENZA
        </Button>
      </div>

      <div className="rounded-sm border border-border bg-card divide-y divide-border">
        {sessions.map((s) => (
          <div key={s.id} className="flex items-center gap-1.5 pr-2">
            <button type="button" data-testid={`emergency-session-${s.id}`} onClick={() => openSession(s.id)}
              className="flex-1 flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-left hover:bg-secondary/50 transition-colors duration-150">
              <div>
                <div className="text-sm font-semibold">{dataOra(s.started_at)}</div>
                <div className="text-[11px] text-muted-foreground">
                  {s.created_by}{s.note ? ` · ${s.note}` : ""}
                  {s.closed_at ? " · CHIUSA" : " · IN CORSO"}
                </div>
              </div>
              <div className="flex gap-3 text-xs font-semibold tabular-nums">
                <span>Compilati: {s.counters?.compiled || 0}</span>
                <span className="text-emerald-700 dark:text-emerald-300">In azienda: {s.counters?.present || 0}</span>
                <span className="text-sky-700 dark:text-sky-300">Evacuati: {s.counters?.evacuated || 0}</span>
                <span className="text-rose-700 dark:text-rose-300">Feriti: {s.counters?.injured || 0}</span>
              </div>
            </button>
            {canDelete && (
              <button type="button" title="Elimina sessione" data-testid={`emergency-delete-${s.id}`}
                className="shrink-0 rounded-sm p-1.5 text-rose-600 hover:text-rose-700"
                onClick={() => removeSession(s)}>
                <Trash size={16} />
              </button>
            )}
          </div>
        ))}
        {sessions.length === 0 && (
          <div className="px-3 py-8 text-center text-muted-foreground" data-testid="emergency-no-sessions">
            Nessuna emergenza registrata
          </div>
        )}
      </div>
    </div>
  );
}
